package com.tovtalk.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.text.BasicText
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withLink
import androidx.compose.ui.text.withStyle
import com.tovtalk.utils.constants.AppConstants

private val HashtagRegex = AppConstants.HASHTAG_PATTERN.toRegex()

/**
 * Renders text with clickable, colored hashtags.
 * Tapping a hashtag calls [onHashtagClick] with the tag (without #).
 */
@Composable
fun ClickableHashtagText(
    text: String,
    modifier: Modifier = Modifier,
    style: androidx.compose.ui.text.TextStyle = MaterialTheme.typography.bodyMedium,
    color: androidx.compose.ui.graphics.Color = MaterialTheme.colorScheme.onSurface,
    hashtagColor: androidx.compose.ui.graphics.Color = MaterialTheme.colorScheme.primary,
    maxLines: Int = Int.MAX_VALUE,
    onHashtagClick: (String) -> Unit = {},
    onClick: () -> Unit = {},
) {
    val annotatedString = remember(text, color, hashtagColor) {
        buildAnnotatedString {
            var lastIndex = 0
            HashtagRegex.findAll(text).forEach { match ->
                // Append text before the hashtag
                if (match.range.first > lastIndex) {
                    withStyle(SpanStyle(color = color)) {
                        append(text.substring(lastIndex, match.range.first))
                    }
                }
                // Append the hashtag with color and link annotation
                val tag = match.value.removePrefix("#")
                withLink(
                    LinkAnnotation.Clickable(
                        tag = "hashtag",
                        styles = TextLinkStyles(style = SpanStyle(color = hashtagColor)),
                        linkInteractionListener = { onHashtagClick(tag) }
                    )
                ) {
                    append(match.value)
                }
                lastIndex = match.range.last + 1
            }
            // Append remaining text
            if (lastIndex < text.length) {
                withStyle(SpanStyle(color = color)) {
                    append(text.substring(lastIndex))
                }
            }
        }
    }

    BasicText(
        text = annotatedString,
        modifier = modifier.clickable(
            indication = null,
            interactionSource = null,
            onClick = onClick
        ),
        style = style.copy(color = color),
        maxLines = maxLines,
    )
}
